export type CityFact = {
  slug: string;
  name: string;
  parks: string;
  urbanContext: string;
  demandNotes: string;
  priceMultiplier: number;
};

export const CITY_SLUGS = [
  'paris',
  'marseille',
  'lyon',
  'toulouse',
  'bordeaux',
  'nice',
  'lille',
  'nantes',
  'strasbourg',
  'montpellier',
  'rennes',
  'grenoble',
  'rouen',
  'dijon',
  'tours',
  'angers',
  'reims',
  'toulon',
  'clermont-ferrand',
  'saint-etienne',
] as const;

export type CitySlug = (typeof CITY_SLUGS)[number];

export const CITY_FACTS: Record<CitySlug, CityFact> = {
  paris: {
    slug: 'paris',
    name: 'Paris',
    parks: 'Bois de Boulogne, Buttes-Chaumont, parc Monceau et quais de Seine',
    urbanContext:
      'appartements, transports en commun, forte densité humaine et canine, nombreux espaces verts partagés',
    demandNotes:
      'rappel en ville, marche en laisse dans les rues animées, gestion des stimulations et socialisation en environnement urbain',
    priceMultiplier: 1.15,
  },
  marseille: {
    slug: 'marseille',
    name: 'Marseille',
    parks: 'Parc Borély, calanques accessibles et collines du sud',
    urbanContext:
      'relief accidenté, quartiers variés, climat méditerranéen et promenades entre mer et collines',
    demandNotes:
      'adaptation à la chaleur, socialisation en milieu animé et promenades sécurisées sur terrains variés',
    priceMultiplier: 0.98,
  },
  lyon: {
    slug: 'lyon',
    name: 'Lyon',
    parks: 'Parc de la Tête d’Or, berges du Rhône et collines de la Croix-Rousse',
    urbanContext:
      'métropole dynamique mêlant centre historique, quartiers résidentiels et zones piétonnes',
    demandNotes:
      'éducation en appartement, promenades en groupes réduits et habituation aux espaces publics fréquentés',
    priceMultiplier: 1.08,
  },
  toulouse: {
    slug: 'toulouse',
    name: 'Toulouse',
    parks: 'Jardin japonais, Prairie des Filtres et canal du Midi',
    urbanContext:
      'ville rose en pleine croissance, nombreuses familles et quartiers résidentiels propices aux chiens',
    demandNotes:
      'accompagnement éducatif pour chiots récemment adoptés et services à domicile en périphérie',
    priceMultiplier: 1.0,
  },
  bordeaux: {
    slug: 'bordeaux',
    name: 'Bordeaux',
    parks: 'Parc bordelais, quais de Garonne et Jardin public',
    urbanContext:
      'centre-ville piéton, quartiers résidentiels et bassin attractif pour les propriétaires actifs',
    demandNotes:
      'marche en laisse le long des quais, garde pendant les déplacements et éducation positive en milieu urbain',
    priceMultiplier: 1.06,
  },
  nice: {
    slug: 'nice',
    name: 'Nice',
    parks: 'Promenade des Anglais, parc Phoenix et collines niçoises',
    urbanContext:
      'Côte d’Azur, tourisme saisonnier, climat doux et forte demande de garde et promenade',
    demandNotes:
      'garde pendant les absences estivales, adaptation au flux touristique et promenades matinales avant la chaleur',
    priceMultiplier: 1.12,
  },
  lille: {
    slug: 'lille',
    name: 'Lille',
    parks: 'Citadelle, parc Jean-Baptiste Lebas et Flandres proches',
    urbanContext:
      'métropole dense du nord, appartements et maisons de ville, hivers longs favorisant les activités intérieures',
    demandNotes:
      'socialisation en intérieur, éducation positive et garde à domicile pour actifs du nord',
    priceMultiplier: 0.97,
  },
  nantes: {
    slug: 'nantes',
    name: 'Nantes',
    parks: 'Île de Versailles, Erdre et bords de Loire',
    urbanContext:
      'agglomération en croissance, cadre de vie vert et nombreuses familles avec chiens',
    demandNotes:
      'éducation du chiot, promenades le long de l’Erdre et services canins en Loire-Atlantique',
    priceMultiplier: 1.02,
  },
  strasbourg: {
    slug: 'strasbourg',
    name: 'Strasbourg',
    parks: 'Orangerie, Robertsau et Ill traversant la ville',
    urbanContext:
      'Eurométropole transfrontalière, centre historique et quartiers résidentiels verdoyants',
    demandNotes:
      'habituation aux espaces publics, éducation en milieu urbain et garde pour frontaliers actifs',
    priceMultiplier: 1.03,
  },
  montpellier: {
    slug: 'montpellier',
    name: 'Montpellier',
    parks: 'Bois de Montpellier-Daunac, Lez et écusson piéton',
    urbanContext:
      'forte croissance démographique, nombreux appartements et étudiants propriétaires de chiens',
    demandNotes:
      'éducation en appartement, garde flexible et loisirs canins adaptés au climat méditerranéen',
    priceMultiplier: 1.04,
  },
  rennes: {
    slug: 'rennes',
    name: 'Rennes',
    parks: 'Thabor, Vilaine et parcs de la métropole bretonne',
    urbanContext:
      'capitale régionale jeune, forte communauté canine et cadre propice aux promenades',
    demandNotes:
      'accompagnement éducatif pour chiots adoptés localement et services de garde en semaine',
    priceMultiplier: 1.0,
  },
  grenoble: {
    slug: 'grenoble',
    name: 'Grenoble',
    parks: 'Parc Paul Mistral, Bastille et sentiers des Quatre Montagnes',
    urbanContext:
      'ville entourée de montagnes, chiens urbains et de montagne, relief et météo contrastés',
    demandNotes:
      'rappel en zone naturelle, gestion de l’excitation en descente et adaptation aux variations météo',
    priceMultiplier: 1.01,
  },
  rouen: {
    slug: 'rouen',
    name: 'Rouen',
    parks: 'Jardin des Plantes, berges de Seine et forêt de Roumare',
    urbanContext: 'centre historique pentu, quais, métropole Seine-Normande et communes de collines',
    demandNotes: 'promenades urbaines, garde le week-end, éducation à domicile hors centre touristique',
    priceMultiplier: 0.98,
  },
  dijon: {
    slug: 'dijon',
    name: 'Dijon',
    parks: 'Parc de la Colombière, Darcy et faubourgs du Grand Dijon',
    urbanContext: 'centre dense, parcs, communes viticoles et résidentielles de Côte-d’Or',
    demandNotes: 'éducation, pet-sitting et toilettage autant en centre qu’en périphérie',
    priceMultiplier: 0.97,
  },
  tours: {
    slug: 'tours',
    name: 'Tours',
    parks: 'Prébendes, berges de Loire et Cher',
    urbanContext: 'agglomération ligérienne, quartiers résidentiels, flux touristique vers les châteaux',
    demandNotes: 'garde et promenades très saisonnières, éducation chiot en appartement du centre',
    priceMultiplier: 0.96,
  },
  angers: {
    slug: 'angers',
    name: 'Angers',
    parks: 'Lac de Maine, Mail et communes du Maine-et-Loire',
    urbanContext: 'chiens en appartement et maisons de banlieue, ville à taille humaine',
    demandNotes: 'socialisation, éducation positive et garde à domicile en semaine',
    priceMultiplier: 0.96,
  },
  reims: {
    slug: 'reims',
    name: 'Reims',
    parks: 'Butte Saint-Nicaise, promenade du Jard et villages viticoles autour',
    urbanContext: 'centre dense, périphérie pavillonnaire, déplacements vers les coteaux',
    demandNotes: 'promenades urbaines, garde le week-end, séances d’éducation à domicile',
    priceMultiplier: 0.97,
  },
  toulon: {
    slug: 'toulon',
    name: 'Toulon',
    parks: 'Mont Faron, rade et collines du Var',
    urbanContext: 'chaleur, littoral, collines, tourisme estival',
    demandNotes: 'promenades tôt le matin, garde en période estivale, toilettage',
    priceMultiplier: 1.02,
  },
  'clermont-ferrand': {
    slug: 'clermont-ferrand',
    name: 'Clermont-Ferrand',
    parks: 'Jardin Lecoq, plateau de Gergovie et accès aux volcans',
    urbanContext: 'ville au pied des Puys, week-ends randonnée, hivers contrastés',
    demandNotes: 'éducateurs, promeneurs et gardes adaptés aux sorties montagne',
    priceMultiplier: 0.95,
  },
  'saint-etienne': {
    slug: 'saint-etienne',
    name: 'Saint-Étienne',
    parks: 'Parc de l’Europe, collines du Forez et communes de la métropole',
    urbanContext: 'relief, parcs, mix ville / périphérie industrielle reconvertie',
    demandNotes: 'garde, éducation et promenades en ville comme en périphérie',
    priceMultiplier: 0.94,
  },
};

export function formatPriceRange(min: number, max: number, multiplier: number): string {
  const lo = Math.round(min * multiplier);
  const hi = Math.round(max * multiplier);
  return `${lo} à ${hi} €`;
}
